import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Layers, Users } from "lucide-react";

export default function FacultyDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/dashboard/faculty").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <Skeleton className="h-96 rounded-2xl" />;
    const s = data.stats;
    return (
        <div className="space-y-8" data-testid="faculty-dashboard">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Faculty workspace</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Today's queue</h1>
            </div>
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
                {[
                    [Layers, "My batches", s.my_batches, "from-[#726AF0] to-[#5B5BF0]"],
                    [Users, "My students", s.my_students, "from-[#E64AA1] to-[#EE46A3]"],
                    [FileText, "Pending submissions", s.pending_submissions, "from-[#FFA81B] to-[#E47A00]"],
                ].map(([Icon, label, val, grad]) => (
                    <div key={label} className="mx-card p-5" data-testid={`faculty-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                                <div className="font-display text-3xl mt-2">{val}</div>
                            </div>
                            <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-white shadow-mx-md`}>
                                <Icon className="h-5 w-5" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Card className="mx-card">
                <CardContent className="p-6">
                    <h3 className="font-bold mb-4">Submissions awaiting grading</h3>
                    {data.pending_submissions.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">All caught up 🎉</div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {data.pending_submissions.map((s) => (
                                <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`pending-submission-${s.id}`}>
                                    <div className="h-9 w-9 rounded-lg bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center"><FileText className="h-4 w-4 text-[#726AF0]" /></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm truncate">{s.assignment_title}</div>
                                        <div className="text-xs text-muted-foreground">{s.student_name} • {new Date(s.submitted_at).toLocaleDateString()}</div>
                                    </div>
                                    <Link to={`/faculty/submissions`}>
                                        <Button size="sm" className="mx-btn-primary text-xs h-8">Grade</Button>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Card className="mx-card">
                <CardContent className="p-6">
                    <h3 className="font-bold mb-4">My batches</h3>
                    {data.batches.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No batches assigned</div> :
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {data.batches.map((b) => (
                                <div key={b.id} className="border border-border rounded-xl p-4" data-testid={`faculty-batch-${b.id}`}>
                                    <div className="font-bold">{b.name}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Status: {b.status}</div>
                                </div>
                            ))}
                        </div>
                    }
                </CardContent>
            </Card>
        </div>
    );
}
